import { createSlice } from "@reduxjs/toolkit";

const initialState = {
	// Whether the modal is currently visible
	isOpen: false,

	// ── Section A: Ranking metric ──────────────────────────────────────────
	// Which metric the user wants to rank MSAs by
	rankingMetric: "jobs", // 'jobs' | 'location_quotient' | 'age_distribution' | 'labor_wage'

	// Only used when rankingMetric = 'age_distribution'
	// Stores label strings exactly as displayed, e.g. ['20-24', '25-29']
	selectedAgeGroups: [],

	// Only used when rankingMetric = 'labor_wage'
	// e.g. 'Percentile10' | 'Percentile25' | 'Median' | 'Average' | 'Percentile75' | 'Percentile90'
	wagePercentile: null,

	// ── Section B: Filters ─────────────────────────────────────────────────
	// All 50 states checked by default -- visually communicates that the
	// search is national. The API treats all-50-selected the same as no
	// state filter (omits the field entirely) so there is no extra backend cost.
	selectedStates: [
		"AL",
		"AK",
		"AZ",
		"AR",
		"CA",
		"CO",
		"CT",
		"DE",
		"FL",
		"GA",
		"HI",
		"ID",
		"IL",
		"IN",
		"IA",
		"KS",
		"KY",
		"LA",
		"ME",
		"MD",
		"MA",
		"MI",
		"MN",
		"MS",
		"MO",
		"MT",
		"NE",
		"NV",
		"NH",
		"NJ",
		"NM",
		"NY",
		"NC",
		"ND",
		"OH",
		"OK",
		"OR",
		"PA",
		"RI",
		"SC",
		"SD",
		"TN",
		"TX",
		"UT",
		"VT",
		"VA",
		"WA",
		"WV",
		"WI",
		"WY",
	],

	// Labor pool slider handle positions
	laborPoolMin: 0,
	laborPoolMax: null, // Set from labor_pool_max returned by wage-range API on modal open

	// Labor pool slider track bounds (populated from wage-range API response)
	// These update whenever the state selection changes, just like the wage slider
	laborPoolSliderMin: null,
	laborPoolSliderMax: null,

	// Target wage slider handle positions (annual dollar values)
	targetWageMin: null, // Set from communityProfile.currentMsaWages.percentile10Annual
	targetWageMax: null, // Set from communityProfile.currentMsaWages.percentile90Annual

	// ── Target Wage slider track bounds ────────────────────────────────────
	// These come from the wage-range API and define how far the track extends
	wageRangeSliderMin: null,
	wageRangeSliderMax: null,
	wageRangeLoading: false,

	// ── Search results ─────────────────────────────────────────────────────
	// null  = not yet fetched
	// []    = fetched but no results matched the filters
	// [...] = up to 5 ranked results
	results: null,

	// Summary counts returned alongside results
	// { total_msas_evaluated, total_passed_demographic_filter, total_passed_wage_filter, ranking_metric, state_filter, soc_name }
	resultsMeta: null,

	resultsLoading: false,
	resultsError: null,

	// ── View state ─────────────────────────────────────────────────────────
	// 'form'    = shows Section A + Section B
	// 'results' = shows result cards (replaces form content inside the same modal)
	currentView: "form",
};

const betterMarketSlice = createSlice({
	name: "betterMarket",
	initialState,
	reducers: {
		// Open the modal
		openModal(state) {
			state.isOpen = true;
		},

		// Close the modal
		closeModal(state) {
			state.isOpen = false;
		},

		// Reset everything back to a clean slate (called every time the modal opens)
		resetModal() {
			return initialState;
		},

		// Set which ranking metric the user has chosen in Section A
		setRankingMetric(state, action) {
			state.rankingMetric = action.payload;
		},

		// Add an age group if it is not already selected, remove it if it is
		toggleAgeGroup(state, action) {
			const group = action.payload;
			const index = state.selectedAgeGroups.indexOf(group);
			if (index >= 0) {
				state.selectedAgeGroups.splice(index, 1);
			} else {
				state.selectedAgeGroups.push(group);
			}
		},

		// Set the wage percentile the user selected (only relevant for labor_wage metric)
		setWagePercentile(state, action) {
			state.wagePercentile = action.payload;
		},

		// Add a state code if not already selected, remove it if it is
		toggleState(state, action) {
			const code = action.payload;
			const index = state.selectedStates.indexOf(code);
			if (index >= 0) {
				state.selectedStates.splice(index, 1);
			} else {
				state.selectedStates.push(code);
			}
		},

		// Replace the entire selected states array at once.
		// Used by the Select All / Deselect All checkbox.
		setSelectedStates(state, action) {
			state.selectedStates = action.payload;
		},

		// Labor Pool slider left handle
		setLaborPoolMin(state, action) {
			state.laborPoolMin = action.payload;
		},

		// Labor Pool slider right handle
		setLaborPoolMax(state, action) {
			state.laborPoolMax = action.payload;
		},

		// Target Wage slider left handle (annual dollar amount)
		setTargetWageMin(state, action) {
			state.targetWageMin = action.payload;
		},

		// Target Wage slider right handle (annual dollar amount)
		setTargetWageMax(state, action) {
			state.targetWageMax = action.payload;
		},

		// Update the Target Wage slider track bounds from the wage-range API response
		setWageRange(state, action) {
			state.wageRangeSliderMin = action.payload.sliderMin;
			state.wageRangeSliderMax = action.payload.sliderMax;
		},

		// Update the Labor Pool slider track bounds from the wage-range API response.
		// On first load (before the API has ever responded), open the slider fully
		// so both handles sit at the track extremes. On subsequent calls (when the
		// user has changed state selection), clamp handles that are now outside the
		// new bounds but leave handles the user has intentionally moved alone.
		setLaborPoolRange(state, action) {
			const isFirstLoad = state.laborPoolSliderMin === null;

			state.laborPoolSliderMin = action.payload.sliderMin;
			state.laborPoolSliderMax = action.payload.sliderMax;

			if (isFirstLoad) {
				// Start fully open -- left handle at floor, right handle at ceiling
				state.laborPoolMin = action.payload.sliderMin;
				state.laborPoolMax = action.payload.sliderMax;
				return;
			}

			// Clamp handles if the new state-scoped bounds are narrower than
			// where the user left them
			if (state.laborPoolMin < action.payload.sliderMin) {
				state.laborPoolMin = action.payload.sliderMin;
			}
			if (state.laborPoolMax > action.payload.sliderMax) {
				state.laborPoolMax = action.payload.sliderMax;
			}
		},

		// Show or hide the loading indicator on the Target Wage slider
		setWageRangeLoading(state, action) {
			state.wageRangeLoading = action.payload;
		},

		// Show or hide the loading indicator on the See Results button
		setResultsLoading(state, action) {
			state.resultsLoading = action.payload;
		},

		// Store the ranked results and the summary metadata from the find API
		setResults(state, action) {
			state.results = action.payload.results;
			state.resultsMeta = action.payload.meta;
			state.message = action.payload.message;
		},

		// Store an error message related to the results request
		setResultsError(state, action) {
			state.resultsError = action.payload;
		},

		// Switch between the form view and the results view inside the modal
		setCurrentView(state, action) {
			state.currentView = action.payload;
		},
	},
});

export const {
	openModal,
	closeModal,
	resetModal,
	setRankingMetric,
	toggleAgeGroup,
	setWagePercentile,
	toggleState,
	setSelectedStates,
	setLaborPoolMin,
	setLaborPoolMax,
	setTargetWageMin,
	setTargetWageMax,
	setWageRange,
	setLaborPoolRange,
	setWageRangeLoading,
	setResultsLoading,
	setResults,
	setResultsError,
	setCurrentView,
} = betterMarketSlice.actions;

export default betterMarketSlice.reducer;
